import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useModalStyles } from "./useModalStyles";
import { displayActivityUser } from "../../utils/activityCenterUtils";

export type ActivityEntry = {
  id: string | number;
  type: string;
  title: string;
  description?: string;
  descriptionSegments?: { text: string; highlight?: boolean }[];
  timestamp: string;
  user?: string;
  chips?: { label: string; color?: string }[];
};

export type ActivityTypeConfig = {
  label: string;
  icon: React.ReactNode;
  color: string;
};

export type ActivityTimelineProps = {
  entries: ActivityEntry[];
  typeConfig: Record<string, ActivityTypeConfig>;
  emptyText?: string;
};

const ACCESS_TYPES = ["VIEW", "ACCESS"];
const CREATED_TYPES = ["CREATE", "ADD"];
const MODIFIED_TYPES = ["MODIFY", "UPDATE"];
const CREATED_MODIFIED_KEY = "CREATED_MODIFIED";
const ACCESS_KEY = "ACCESS";

type TimelineTypeGroup = {
  key: string;
  label: string;
  types: string[];
  color: string;
  count: number;
};

const formatTimestamp = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const initialsOf = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ entries, typeConfig, emptyText = "No activity yet." }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { tokens } = useModalStyles();
  const [activeType, setActiveType] = useState<string>("ALL");

  const typeGroups = useMemo((): TimelineTypeGroup[] => {
    const present = new Set(entries.map((entry) => entry.type));
    const groups: TimelineTypeGroup[] = [];
    const createdModifiedTypes = CREATED_TYPES.concat(MODIFIED_TYPES).filter((type) => present.has(type));
    if (createdModifiedTypes.length > 0) {
      const colorType = createdModifiedTypes.find((type) => MODIFIED_TYPES.indexOf(type) >= 0) || createdModifiedTypes[0];
      groups.push({
        key: CREATED_MODIFIED_KEY,
        label: "Modified & Created",
        types: createdModifiedTypes,
        color: typeConfig[colorType]?.color || tokens.accent,
        count: entries.filter((entry) => createdModifiedTypes.includes(entry.type)).length,
      });
    }

    const handled = new Set(createdModifiedTypes.concat(ACCESS_TYPES));
    present.forEach((type) => {
      if (handled.has(type)) return;
      groups.push({
        key: type,
        label: typeConfig[type]?.label || type,
        types: [type],
        color: typeConfig[type]?.color || tokens.accent,
        count: entries.filter((entry) => entry.type === type).length,
      });
    });

    const accessTypes = ACCESS_TYPES.filter((type) => present.has(type));
    if (accessTypes.length > 0) {
      groups.push({
        key: ACCESS_KEY,
        label: "Access",
        types: accessTypes,
        color: typeConfig[accessTypes[0]]?.color || "#64748b",
        count: entries.filter((entry) => accessTypes.includes(entry.type)).length,
      });
    }
    return groups;
  }, [entries, typeConfig, tokens.accent]);

  const allCount = useMemo(
    () => entries.filter((entry) => ACCESS_TYPES.indexOf(entry.type) < 0).length,
    [entries]
  );

  const filteredEntries = useMemo(() => {
    if (activeType === "ALL") return entries.filter((entry) => ACCESS_TYPES.indexOf(entry.type) < 0);
    const group = typeGroups.find((item) => item.key === activeType);
    if (!group) return entries.filter((entry) => ACCESS_TYPES.indexOf(entry.type) < 0);
    return entries.filter((entry) => group.types.includes(entry.type));
  }, [entries, activeType, typeGroups]);

  const chipSx = (selected: boolean, color: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.6,
    px: 1.4,
    py: 0.6,
    borderRadius: "999px",
    fontSize: "0.78rem",
    fontWeight: 700,
    cursor: "pointer",
    border: `1px solid ${selected ? color : isDark ? "rgba(148,163,184,0.28)" : "rgba(15,23,42,0.14)"}`,
    backgroundColor: selected ? `${color}22` : isDark ? "rgba(148,163,184,0.06)" : "rgba(15,23,42,0.03)",
    color: selected ? color : tokens.textSecondary,
    transition: "all 0.15s ease",
    whiteSpace: "nowrap" as const,
    "&:hover": { borderColor: color, color },
  });

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
        <Box
          data-testid="activity-type-ALL"
          sx={chipSx(activeType === "ALL", tokens.accent)}
          onClick={() => setActiveType("ALL")}
        >
          All ({allCount})
        </Box>
        {typeGroups.map((group) => (
          <Box
            key={group.key}
            data-testid={`activity-type-${group.key}`}
            sx={chipSx(activeType === group.key, group.color)}
            onClick={() => setActiveType(group.key)}
          >
            {group.label} ({group.count})
          </Box>
        ))}
      </Box>

      {filteredEntries.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center", color: tokens.textSecondary, fontSize: "0.9rem" }}>{emptyText}</Box>
      ) : (
        <Box>
          {filteredEntries.map((entry, index) => {
            const config = typeConfig[entry.type] || { label: entry.type, icon: null, color: tokens.accent };
            const isLast = index === filteredEntries.length - 1;
            const userLabel = displayActivityUser(entry.user);
            return (
              <Box key={entry.id} sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${config.color}1f`,
                      border: `1.5px solid ${config.color}`,
                      color: config.color,
                      flexShrink: 0,
                      "& svg": { fontSize: "1.15rem" },
                    }}
                  >
                    {config.icon}
                  </Box>
                  {!isLast ? (
                    <Box
                      sx={{
                        width: "2px",
                        flex: 1,
                        minHeight: 28,
                        backgroundColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.1)",
                        my: 0.5,
                      }}
                    />
                  ) : null}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 3 }}>
                  <Box
                    sx={{
                      borderRadius: "12px",
                      border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
                      backgroundColor: isDark ? "rgba(148,163,184,0.04)" : "rgba(15,23,42,0.015)",
                      p: 1.75,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: tokens.textPrimary }}>
                        {entry.title}
                      </Typography>
                      <Typography sx={{ fontSize: "0.76rem", color: tokens.textSecondary, whiteSpace: "nowrap" }}>
                        {formatTimestamp(entry.timestamp)}
                      </Typography>
                    </Box>
                    {entry.descriptionSegments && entry.descriptionSegments.length > 0 ? (
                      <Typography sx={{ mt: 0.5, fontSize: "0.84rem", color: tokens.textSecondary, lineHeight: 1.5 }}>
                        {entry.descriptionSegments.map((segment, segmentIndex) =>
                          segment.highlight ? (
                            <Box
                              key={segmentIndex}
                              component="span"
                              data-testid="activity-highlight"
                              sx={{
                                display: "inline",
                                color: tokens.textPrimary,
                                fontWeight: 700,
                              }}
                            >
                              {segment.text}
                            </Box>
                          ) : (
                            <Box key={segmentIndex} component="span">
                              {segment.text}
                            </Box>
                          )
                        )}
                      </Typography>
                    ) : entry.description ? (
                      <Typography sx={{ mt: 0.5, fontSize: "0.84rem", color: tokens.textSecondary, lineHeight: 1.5 }}>
                        {entry.description}
                      </Typography>
                    ) : null}
                    {entry.chips && entry.chips.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {entry.chips.map((chip, chipIndex) => (
                          <Box
                            key={chipIndex}
                            sx={{
                              px: 1.1,
                              py: 0.3,
                              borderRadius: "999px",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              backgroundColor: `${chip.color || tokens.accent}1f`,
                              color: chip.color || tokens.accent,
                            }}
                          >
                            {chip.label}
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.25 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
                          color: tokens.textSecondary,
                        }}
                      >
                        {initialsOf(userLabel)}
                      </Box>
                      <Typography sx={{ fontSize: "0.76rem", color: tokens.textSecondary }}>{userLabel}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ActivityTimeline;
